import { Modal } from "antd";
import classNames from "classnames";
import { ReactNode, useMemo } from "react";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";

import styles from "./style.module.scss";

export interface BaseModalProps {
  isOpen: boolean;
  variant: Variant;
  headers?: {
    title?: string;
    subTitle?: string;
  };
  className?: string;
  children?: ReactNode;
  footer?: ReactNode | null;
  onClose: () => void;
  icon?: React.ReactNode;
}

export type Variant = "error" | "success" | "info" | "general";
const variantIcons: Record<Variant, ReactNode> = {
  error: <CloseCircleOutlined />,
  success: <CheckCircleOutlined />,
  info: <InfoCircleOutlined />,
  general: null,
};

const BaseModal = ({
  headers,
  isOpen,
  variant,
  children,
  className,
  footer = null,
  onClose,
  icon,
}: BaseModalProps) => {
  const baseModalClassnames = classNames(
    styles.baseModal,
    className,
    styles[variant],
  );

  const displayIcon = icon ? icon : variantIcons[variant];

  const titleComponent = useMemo(() => {
    if (!headers) return null;

    return (
      <div className={styles.modalTitle}>
        <div className={styles.titleIcon}>
          {displayIcon && <div data-testid="variant-icon">{displayIcon}</div>}
          <span>{headers?.title}</span>
        </div>
        <span className={styles.subTitle}>{headers?.subTitle}</span>
      </div>
    );
  }, [headers, displayIcon]);

  return (
    <Modal
      className={baseModalClassnames}
      title={titleComponent}
      footer={footer}
      destroyOnClose
      open={isOpen}
      onCancel={onClose}
    >
      <div className={styles.modalContent}>{children}</div>
    </Modal>
  );
};

export default BaseModal;
